import React, { useState, useEffect } from 'react';
import { doc, getDoc, updateDoc, setDoc, deleteDoc, serverTimestamp, collection, getDocs, query, where, limit } from 'firebase/firestore';
import { db } from '../firebase';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { Switch } from '@headlessui/react';
import { Filter } from 'bad-words';
import VideoUpload from './VideoUpload';

const EditStory = ({ story, onClose, currentUser }) => {
  // Add console logs to see what data we're receiving
  console.log('EditStory Modal - Received Props:', {
    story,
    currentUser,
    storyId: story?.id,
    storyText: story?.text,
    storyMetrics: story?.metrics,
    storySharing: story?.sharing
  });

  const [formData, setFormData] = useState({
    text: story?.text || '',
    metrics: {
      spiritualDiscussions: story?.metrics?.spiritualDiscussions || 0,
      heardGospel: story?.metrics?.heardGospel || 0,
      experiencedLove: story?.metrics?.experiencedLove || 0,
      believersEncouraged: story?.metrics?.believersEncouraged || 0,
      newFollowers: story?.metrics?.newFollowers || 0
    },
    sharing: {
      private: story?.sharing?.private || false,
      organizations: story?.sharing?.organizations || []
    }
  });
  const [storyVideoPlayableURL, setStoryVideoPlayableURL] = useState(story?.playableURL || story?.videoURL || null);
  const [storyVideoStoragePath, setStoryVideoStoragePath] = useState(story?.storagePath || null);
  const [storyPosterURL, setStoryPosterURL] = useState(story?.posterURL || null);
  const [globalOrg, setGlobalOrg] = useState(null);
  const [isWorldwide, setIsWorldwide] = useState(false);
  const [showWorldwideModal, setShowWorldwideModal] = useState(false);
  const [worldwideUnderstandChecked, setWorldwideUnderstandChecked] = useState(false);
  const [worldwideNoClosedCountryChecked, setWorldwideNoClosedCountryChecked] = useState(false);

  // Log formData after initialization
  useEffect(() => {
    console.log('EditStory Modal - Initialized FormData:', formData);
  }, []);

  const [userOrganizations, setUserOrganizations] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch user's organizations and global org
  useEffect(() => {
    const fetchUserOrgs = async () => {
      if (!currentUser?.uid) return;
      try {
        // Load global organization
        try {
          const globalOrgQuery = query(
            collection(db, 'organizations'),
            where('type', '==', 'global'),
            limit(1)
          );
          const globalOrgSnapshot = await getDocs(globalOrgQuery);
          
          if (!globalOrgSnapshot.empty) {
            const orgData = globalOrgSnapshot.docs[0].data();
            setGlobalOrg({
              id: globalOrgSnapshot.docs[0].id,
              name: orgData?.name || 'Together On Mission',
              displayName: 'Worldwide - Everyone',
            });
            // Check if story is already shared worldwide
            if (formData.sharing.organizations.includes(globalOrgSnapshot.docs[0].id)) {
              setIsWorldwide(true);
            }
          } else {
            // Fallback: check for isGlobal flag
            const fallbackQuery = query(
              collection(db, 'organizations'),
              where('isGlobal', '==', true),
              limit(1)
            );
            const fallbackSnapshot = await getDocs(fallbackQuery);
            if (!fallbackSnapshot.empty) {
              const orgData = fallbackSnapshot.docs[0].data();
              setGlobalOrg({
                id: fallbackSnapshot.docs[0].id,
                name: orgData?.name || 'Together On Mission',
                displayName: 'Worldwide - Everyone',
              });
              if (formData.sharing.organizations.includes(fallbackSnapshot.docs[0].id)) {
                setIsWorldwide(true);
              }
            }
          }
        } catch (error) {
          console.error('Error loading global organization:', error);
        }

        const userOrgsRef = collection(db, 'users', currentUser.uid, 'organizations');
        const userOrgsSnap = await getDocs(userOrgsRef);
        
        const activeOrgs = await Promise.all(
          userOrgsSnap.docs
            .filter(doc => doc.data().status === 'active')
            .map(async (membershipDoc) => {
              const membershipData = membershipDoc.data();
              const orgDoc = await getDoc(doc(db, 'organizations', membershipDoc.id));
              
              if (!orgDoc.exists()) return null;

              const orgData = orgDoc.data();
              return {
                id: membershipDoc.id,
                name: orgData.name,
                displayName: orgData.displayName || orgData.name,
                role: membershipData.role,
                status: membershipData.status
              };
            })
        );

        const validOrgs = activeOrgs.filter(org => org !== null);
        setUserOrganizations(validOrgs);
      } catch (error) {
        console.error('Error fetching organizations:', error);
      }
    };

    fetchUserOrgs();
  }, [currentUser, formData.sharing.organizations]);

  const handleMetricChange = (metric, value) => {
    const newValue = Math.max(0, parseInt(value) || 0);
    setFormData(prev => ({
      ...prev,
      metrics: {
        ...prev.metrics,
        [metric]: newValue
      }
    }));
  };

  const handleOrgToggle = (orgId) => {
    setFormData(prev => ({
      ...prev,
      sharing: {
        ...prev.sharing,
        organizations: prev.sharing.organizations.includes(orgId)
          ? prev.sharing.organizations.filter(id => id !== orgId)
          : [...prev.sharing.organizations, orgId]
      }
    }));
  };

  const handleUpdateStory = async () => {
    setIsSubmitting(true);
    try {
      const filter = new Filter();
      const cleanedText = filter.clean(formData.text);
      const storyRef = doc(db, 'users', currentUser.uid, 'stories', story.id);
      
      // Get current story data to preserve fields
      const currentStoryDoc = await getDoc(storyRef);
      const currentStoryData = currentStoryDoc.data();
      
      // Check for pending video compression
      let finalPlayableURL = storyVideoPlayableURL;
      let finalPosterURL = storyPosterURL;
      let finalStoragePath = storyVideoStoragePath;
      
      try {
        const pendingCompressionRef = doc(db, 'pendingVideoCompressions', story.id);
        const pendingCompressionSnap = await getDoc(pendingCompressionRef);
        
        if (pendingCompressionSnap.exists) {
          const compressionData = pendingCompressionSnap.data();
          finalPlayableURL = compressionData.playableURL;
          finalPosterURL = compressionData.posterURL;
          finalStoragePath = compressionData.storagePath;
          // Delete pending record after use
          await deleteDoc(pendingCompressionRef);
        }
      } catch (error) {
        console.error('Error checking pending compression:', error);
      }
      
      // Build updated story data
      const updatedStoryData = {
        text: cleanedText || currentStoryData?.text || '',
        metrics: formData.metrics,
        sharing: {
          private: currentStoryData?.sharing?.private || false,
          organizations: formData.sharing.organizations
        },
        lastUpdated: serverTimestamp()
      };
      
      // Add video fields if video exists
      if (finalPlayableURL) {
        updatedStoryData.playableURL = finalPlayableURL;
        updatedStoryData.videoURL = finalPlayableURL; // Legacy compatibility
        if (finalPosterURL) {
          updatedStoryData.posterURL = finalPosterURL;
        }
        if (finalStoragePath) {
          updatedStoryData.storagePath = finalStoragePath;
        }
      } else if (storyVideoPlayableURL === null && currentStoryData?.playableURL) {
        // Remove video if explicitly removed
        updatedStoryData.playableURL = null;
        updatedStoryData.videoURL = null;
        updatedStoryData.posterURL = null;
        updatedStoryData.storagePath = null;
      }
      
      // If story.id exists, update; otherwise, create new story
      if (story.id) {
        await updateDoc(storyRef, updatedStoryData);
      } else {
        await setDoc(storyRef, {
          ...updatedStoryData,
          authorId: currentUser.uid,
          authorName: currentUser.displayName,
          authorEmail: currentUser.email,
          authorPhotoURL: currentUser.photoURL,
          createdAt: serverTimestamp(),
          status: 'active'
        });
      }

      // Build complete story data for syncing to organizations
      const completeStoryData = {
        ...currentStoryData,
        ...updatedStoryData,
        id: story.id,
        authorId: currentStoryData?.authorId || currentUser.uid,
        authorName: currentStoryData?.authorName || currentUser.displayName,
        authorEmail: currentStoryData?.authorEmail || currentUser.email,
        authorPhotoURL: currentStoryData?.authorPhotoURL || currentUser.photoURL,
        status: currentStoryData?.status || 'active',
        createdAt: currentStoryData?.createdAt || serverTimestamp()
      };

      // Update stories-feed
      const isPrivate = completeStoryData.sharing.private;
      if (isPrivate) {
        // Remove from stories-feed if making private
        await deleteDoc(doc(db, 'stories-feed', story.id)).catch(() => {
          // Ignore if doesn't exist
        });
      } else {
        // Add/update in stories-feed if not private
        await setDoc(doc(db, 'stories-feed', story.id), completeStoryData);
      }

      // Update organization references
      const oldOrgIds = story.sharing?.organizations || [];
      const newOrgIds = formData.sharing.organizations;
      
      // Remove from unselected orgs
      const removedOrgIds = oldOrgIds.filter(id => !newOrgIds.includes(id));
      await Promise.all(
        removedOrgIds.map(orgId => 
          deleteDoc(doc(db, 'organizations', orgId, 'stories', story.id)).catch(() => {
            // Ignore if doesn't exist
          })
        )
      );

      // Add or Update in newly selected orgs - save FULL story data (not just metadata)
      await Promise.all(
        newOrgIds.map(async (orgId) => {
          const orgStoryRef = doc(db, 'organizations', orgId, 'stories', story.id);
          // Use set() to ensure full data is saved
          await setDoc(orgStoryRef, completeStoryData);
        })
      );

      onClose();
    } catch (error) {
      console.error('Error updating story:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-2 z-50">
      <div className="bg-white rounded-xl w-full max-w-xl max-h-[90vh] overflow-y-auto">
        <div className="p-3 space-y-3">
          {/* Header */}
          <div className="text-center relative pb-2">
            <h2 className="text-lg font-bold text-gray-900">Edit Story</h2>
            <button 
              onClick={onClose} 
              className="absolute right-0 top-0 text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          {/* Story Text */}
          <div className="bg-gray-50 rounded-lg p-2">
            <textarea
              rows="4"
              className="block w-full bg-transparent border-0 focus:ring-0 text-sm resize-none placeholder:text-gray-400"
              placeholder="Type your story here... (optional if video is present)"
              value={formData.text}
              onChange={(e) => setFormData(prev => ({ ...prev, text: e.target.value }))}
            />
          </div>

          {/* Video Upload/Edit */}
          {(story?.playableURL || story?.videoURL || storyVideoPlayableURL) && (
            <div className="bg-gray-50 rounded-lg p-2">
              <label className="block text-xs font-medium text-gray-700 mb-2">Video Story</label>
              <VideoUpload
                currentVideo={storyVideoPlayableURL || story?.playableURL || story?.videoURL}
                onUploadSuccess={({ playableURL, storagePath }) => {
                  setStoryVideoPlayableURL(playableURL);
                  setStoryVideoStoragePath(storagePath);
                }}
                onRemove={() => {
                  setStoryVideoPlayableURL(null);
                  setStoryVideoStoragePath(null);
                  setStoryPosterURL(null);
                }}
              />
            </div>
          )}

          {/* Compact Metrics Grid */}
          <div className="bg-gray-50 rounded-lg p-2">
            <h3 className="text-xs font-medium text-gray-700 mb-2">Impact Metrics</h3>
            <div className="grid grid-cols-2 gap-2 text-sm">
              {[
                { id: 'experiencedLove', label: "God's Love" },
                { id: 'spiritualDiscussions', label: 'Discussions' },
                { id: 'heardGospel', label: 'Gospel' },
                { id: 'believersEncouraged', label: 'Encouraged' },
                { id: 'newFollowers', label: 'New Followers' }
              ].map((metric) => (
                <div key={metric.id} className="flex items-center justify-between bg-white rounded px-2 py-1.5">
                  <span className="text-xs text-gray-600">{metric.label}</span>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => handleMetricChange(metric.id, formData.metrics[metric.id] - 1)}
                      className="w-5 h-5 flex items-center justify-center text-xs text-gray-500 bg-gray-100 rounded"
                    >
                      -
                    </button>
                    <span className="w-4 text-center text-xs font-medium">
                      {formData.metrics[metric.id]}
                    </span>
                    <button
                      type="button"
                      onClick={() => handleMetricChange(metric.id, formData.metrics[metric.id] + 1)}
                      className="w-5 h-5 flex items-center justify-center text-xs text-gray-500 bg-gray-100 rounded"
                    >
                      +
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Worldwide Sharing Toggle */}
          {globalOrg && (
            <div className="bg-gray-50 rounded-lg p-2">
              <div className="flex items-center justify-between bg-white rounded px-2 py-1.5">
                <div className="flex-1">
                  <span className="text-xs font-medium text-gray-700 block">{globalOrg.displayName}</span>
                  <span className="text-xs text-gray-500">Share with believers worldwide</span>
                </div>
                <div className="relative inline-block w-11 h-5">
                  <input
                    type="checkbox"
                    id="worldwide-switch"
                    checked={isWorldwide}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setIsWorldwide(checked);
                      if (checked) {
                        setShowWorldwideModal(true);
                      } else {
                        // Remove global org from sharing
                        setFormData(prev => ({
                          ...prev,
                          sharing: {
                            ...prev.sharing,
                            organizations: prev.sharing.organizations.filter(id => id !== globalOrg.id)
                          }
                        }));
                      }
                    }}
                    className="peer appearance-none w-11 h-5 bg-gray-200 rounded-full checked:bg-blue-600 cursor-pointer transition-colors duration-300"
                  />
                  <label
                    htmlFor="worldwide-switch"
                    className="absolute top-0 left-0 w-5 h-5 bg-white rounded-full border border-gray-300 shadow-sm transition-transform duration-300 peer-checked:translate-x-6 peer-checked:border-blue-600 cursor-pointer"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Worldwide Sharing Modal */}
          {showWorldwideModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
              <div className="bg-white rounded-lg max-w-md w-full p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Share Worldwide</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Sharing worldwide means your story will be visible to believers around the globe who are part of the Together On Mission community.
                </p>
                <div className="space-y-3 mb-4">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={worldwideUnderstandChecked}
                      onChange={(e) => setWorldwideUnderstandChecked(e.target.checked)}
                      className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">
                      I understand this story will be visible to believers worldwide
                    </span>
                  </label>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={worldwideNoClosedCountryChecked}
                      onChange={(e) => setWorldwideNoClosedCountryChecked(e.target.checked)}
                      className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">
                      I confirm this story does not mention any closed countries or sensitive locations
                    </span>
                  </label>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setShowWorldwideModal(false);
                      setIsWorldwide(false);
                      setWorldwideUnderstandChecked(false);
                      setWorldwideNoClosedCountryChecked(false);
                    }}
                    className="flex-1 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      if (worldwideUnderstandChecked && worldwideNoClosedCountryChecked) {
                        // Add global org to sharing
                        if (globalOrg && !formData.sharing.organizations.includes(globalOrg.id)) {
                          setFormData(prev => ({
                            ...prev,
                            sharing: {
                              ...prev.sharing,
                              organizations: [...prev.sharing.organizations, globalOrg.id]
                            }
                          }));
                        }
                        setShowWorldwideModal(false);
                        setWorldwideUnderstandChecked(false);
                        setWorldwideNoClosedCountryChecked(false);
                      }
                    }}
                    disabled={!worldwideUnderstandChecked || !worldwideNoClosedCountryChecked}
                    className="flex-1 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Confirm
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Compact Organization Sharing */}
          <div className="bg-gray-50 rounded-lg p-2">
            <h3 className="text-xs font-medium text-gray-700 mb-2">Share with Organizations</h3>
            <div className="space-y-1 max-h-48 overflow-y-auto">
              {userOrganizations
                .filter(org => !globalOrg || org.id !== globalOrg.id) // Exclude global org from list (handled separately)
                .map(org => (
                  <div
                    key={org.id}
                    className="flex items-center justify-between bg-white rounded px-2 py-1.5"
                  >
                    <span className="text-xs text-gray-600">{org.displayName}</span>
                    <div className="relative inline-block w-11 h-5">
                      <input
                        type="checkbox"
                        id={`org-switch-${org.id}`}
                        checked={formData.sharing.organizations.includes(org.id)}
                        onChange={() => handleOrgToggle(org.id)}
                        className="peer appearance-none w-11 h-5 bg-gray-200 rounded-full checked:bg-blue-600 cursor-pointer transition-colors duration-300"
                      />
                      <label
                        htmlFor={`org-switch-${org.id}`}
                        className="absolute top-0 left-0 w-5 h-5 bg-white rounded-full border border-gray-300 shadow-sm transition-transform duration-300 peer-checked:translate-x-6 peer-checked:border-blue-600 cursor-pointer"
                      />
                    </div>
                  </div>
                ))}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2 pt-2">
            <button
              onClick={onClose}
              className="flex-1 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors duration-200"
            >
              Cancel
            </button>
            <button
              onClick={handleUpdateStory}
              disabled={isSubmitting}
              className="flex-1 py-2.5 text-sm font-medium text-white bg-[#5ABA47] rounded-lg hover:bg-[#4da53d] disabled:opacity-50 transition-colors duration-200"
            >
              {isSubmitting ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditStory; 